import { createFileRoute } from "@tanstack/react-router";
import { ResourceArticle } from "@/components/ResourceArticle";
import { buildBreadcrumbs } from "@/components/SchemaJsonLd";
import heroImg from "@/assets/service-flake.jpg";

export const Route = createFileRoute("/resources/epoxy-vs-polyaspartic")({
  head: () => ({
    meta: [
      { title: "Epoxy vs Polyaspartic: Side-by-Side Comparison | MLC Epoxy Floors" },
      { name: "description", content: "Cure time, UV stability, durability, chemical resistance, and cost — how traditional epoxy compares to polyaspartic floor coatings." },
      { property: "og:title", content: "Epoxy vs Polyaspartic" },
      { property: "og:description", content: "Side-by-side comparison of two leading garage floor coating systems." },
      { property: "og:url", content: "/resources/epoxy-vs-polyaspartic" },
      { property: "og:image", content: heroImg },
      { property: "og:type", content: "article" },
    ],
    links: [{ rel: "canonical", href: "/resources/epoxy-vs-polyaspartic" }],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify(buildBreadcrumbs([
        { name: "Home", href: "/" },
        { name: "Resources", href: "/resources" },
        { name: "Epoxy vs Polyaspartic", href: "/resources/epoxy-vs-polyaspartic" },
      ])),
    }],
  }),
  component: () => (
    <ResourceArticle
      title="Epoxy vs Polyaspartic"
      intro="Two of the most common garage floor coatings — but they perform very differently. Here's how they actually compare, and when each one makes sense."
      image={heroImg}
      sections={[
        {
          type: "prose",
          heading: "The Short Version",
          body: "Traditional epoxy is a thick, rigid coating that's been the industry standard for 40 years. Polyaspartic is a newer aliphatic polyurea that cures in hours instead of days, doesn't yellow in sunlight, and stays flexible at low temperatures. Most modern professional systems use both — epoxy as the build coat and polyaspartic as the UV-stable top coat.",
        },
        {
          type: "table",
          heading: "Side-by-Side Comparison",
          columns: ["Property", "Traditional Epoxy", "Polyaspartic"],
          rows: [
            ["Cure time (foot traffic)", "16–24 hours", "4–6 hours"],
            ["Total project time", "3–5 days", "1 day"],
            ["UV stability", "Yellows & chalks", "Crystal-clear, will not yellow"],
            ["Vehicle traffic", "5–7 days", "24 hours"],
            ["Chemical resistance", "Good (rated)", "Excellent (rated)"],
            ["Flexibility", "Rigid, can crack", "Flexible, resists impact"],
            ["Temperature application range", "55–90°F", "0–110°F"],
            ["Lifespan", "5–10 years", "15–20+ years"],
            ["Cost (top coat only)", "Lower", "20–40% higher"],
          ],
        },
        {
          type: "list",
          heading: "When Epoxy-Only Makes Sense",
          items: [
            { label: "Indoor warehouse with no sunlight", desc: "Without UV exposure, the yellowing issue disappears." },
            { label: "Tight budget on a low-traffic space", desc: "Storage units, utility rooms, or basements that won't see vehicles." },
            { label: "Thick build coat needed", desc: "Epoxy can be poured at 20+ mils for self-leveling or coving applications." },
          ],
        },
        {
          type: "list",
          heading: "When Polyaspartic Wins",
          items: [
            { label: "Any garage with a window or open door", desc: "UV exposure will yellow pure epoxy within 12–18 months." },
            { label: "You need the space back fast", desc: "Polyaspartic systems are walk-on in 4 hours, drive-on the next day." },
            { label: "Cold or hot weather installation", desc: "Polyaspartic cures down to 0°F — epoxy won't cure properly below 55°F." },
            { label: "Showrooms, workshops, or showpiece garages", desc: "The clarity and depth of polyaspartic over flake or metallic is unmatched." },
          ],
        },
        {
          type: "prose",
          heading: "Our Recommendation",
          body: "For virtually every residential and commercial garage in the Calgary region, we install a hybrid system: a colored epoxy base coat broadcast with vinyl flake, then sealed with two coats of polyaspartic. You get the build and adhesion of epoxy with the UV stability, chemical resistance, and longevity of polyaspartic — for a system that lasts 15–20 years.",
        },
      ]}
    />
  ),
});
